/**
 * Loading one distinct `objectRef` into the geometry every placement of it
 * shares — the half of `world-instancer.ts` that is about *a model*, split out
 * of it because the host reached the 400-line ceiling.
 *
 * One `SharedRef` per ref, built once however many times the region places it:
 * the chosen LOD's triangles as a rotated `BufferGeometry`, its own line faces
 * and its triangles' outlines merged into one segment source
 * (`polygon-outlines.ts` for why the outlines exist at all), and the three
 * non-triangle node kinds a `.TBL` LOD can carry. The hour's dimming is applied
 * here rather than downstream, because it has to land **before** the outline
 * segments are read off the geometry or a road would keep its noon colour over
 * a dusk field.
 */

import * as THREE from "three/webgpu";
import {
  bestDrawableLod,
  createLodGeometry,
  loadBakedModel,
  type BakedModelBillboard,
  type BakedModelBody,
  type BakedModelSphere,
  type BakedModelSpriteSheet,
} from "./baked-model.ts";
import {
  concatSegments,
  lineFaceSegments,
  triangleOutlineSegments,
  type SegmentSource,
} from "./polygon-outlines.ts";
import { placementWorldTransform, type PlacementOrigin } from "../world/placement-transform.ts";
import { SEA_PLATE_DEPTH_M, SEA_SCALE_EXPONENT } from "../world/water-mask.ts";
import { colourToRgb, dimTerrainColour } from "../world/terrain-palette.ts";
import { STATIC_STATE_INDEX } from "./sphere-instances.ts";
import type { GroundSampler } from "./terrain-drape.ts";

/** One placed instance of a baked object, as a region's `.WLD` records it. */
export interface InstancerPlacement {
  readonly objectRef: string | null;
  readonly x: number;
  readonly y: number;
  readonly rotationDegrees: number;
}

/** The model-frame -90° X convention (`world/placement-transform.ts`), as a `Matrix4` rotation for `geometry.rotateX`. */
const MODEL_TO_WORLD_PITCH_RAD = -Math.PI / 2;

/** The chosen LOD's non-triangle nodes, filtered to the state a static instance draws. */
export interface SharedPrimitives {
  /** Sphere nodes — the tree canopies. */
  readonly spheres: readonly BakedModelSphere[];
  /** Sprite nodes, meaningful only alongside `spriteSheet`. */
  readonly billboards: readonly BakedModelBillboard[];
  readonly spriteSheet: BakedModelSpriteSheet | undefined;
}

export interface SharedRef extends SharedPrimitives {
  readonly geometry: THREE.BufferGeometry;
  readonly segments: SegmentSource;
  readonly boundingRadius: number;
}

const EMPTY_SEGMENTS: SegmentSource = { positions: new Float32Array(0), colors: new Float32Array(0), segmentCount: 0 };

/** A ref whose chosen LOD has no triangles still draws if it has lines, spheres or sprites; if it has none of the four it is nothing. */
function nonMeshRef(
  segments: SegmentSource | undefined,
  primitives: SharedPrimitives,
  boundingRadius: number,
): SharedRef | undefined {
  if (!segments && primitives.spheres.length === 0 && primitives.billboards.length === 0) return undefined;
  return { geometry: new THREE.BufferGeometry(), segments: segments ?? EMPTY_SEGMENTS, boundingRadius, ...primitives };
}

/**
 * Walks every vertex colour of a placed terrain mesh down its own run for the
 * hour. Done before the outline segments are read off the geometry, so the
 * one-pixel edges dim with the faces they belong to.
 */
function dimGeometryColours(geometry: THREE.BufferGeometry, shift: number): void {
  const attribute = geometry.getAttribute("color");
  if (!attribute) return;
  const array = attribute.array as Float32Array;
  for (let i = 0; i < array.length; i += 3) {
    const packed =
      (Math.round(array[i]! * 255) << 16) | (Math.round(array[i + 1]! * 255) << 8) | Math.round(array[i + 2]! * 255);
    const [r, g, b] = colourToRgb(dimTerrainColour(packed, shift));
    array[i] = r;
    array[i + 1] = g;
    array[i + 2] = b;
  }
  attribute.needsUpdate = true;
}

export async function loadSharedRef(
  objectRef: string,
  preloaded: BakedModelBody | undefined,
  rampShift: number,
): Promise<SharedRef | undefined> {
  const body = preloaded ?? (await loadBakedModel(`${objectRef}.json`));
  const lod = bestDrawableLod(body.lods);
  const baked = lineFaceSegments(lod?.lines);
  const primitives: SharedPrimitives = {
    spheres: (lod?.spheres ?? []).filter((sphere) => sphere.stateIndex === STATIC_STATE_INDEX),
    billboards: (lod?.billboards ?? []).filter((billboard) => billboard.stateIndex === STATIC_STATE_INDEX),
    spriteSheet: body.spriteSheet,
  };
  if (!lod || lod.triangleCount === 0) {
    // Objects made entirely of 2-point faces (40 of the corpus's `TERRn`
    // slots are roads and rivers with no triangle at any LOD) — or entirely of
    // sprites — still have something to draw.
    return nonMeshRef(baked, primitives, body.boundingRadius);
  }
  const geometry = createLodGeometry(lod);
  geometry.rotateX(MODEL_TO_WORLD_PITCH_RAD);
  // The sea gets a metre of draught, so water is unambiguously *under* land.
  // `london-sea-placements.md` §1.4: an `s = 10` plate is a flat quad at
  // `y = 0`, exactly coplanar with every town's ground pad, whose own `y` is
  // also 0 — and `three/decal-layers.ts`'s 4 mm nudge is per *model*, so it
  // does nothing across two of them. The two z-fight. Done here rather than in
  // the instance matrix so the outline segments read off this geometry a few
  // lines down sink with it. See `world/water-mask.ts` for why a metre.
  if ((body.scaleExponent ?? 0) >= SEA_SCALE_EXPONENT) geometry.translate(0, -SEA_PLATE_DEPTH_M, 0);

  if (rampShift !== 0) dimGeometryColours(geometry, rampShift);

  const outlines = triangleOutlineSegments({
    positions: geometry.getAttribute("position").array,
    colors: geometry.getAttribute("color").array,
  });
  return {
    geometry,
    segments: baked ? concatSegments([outlines, baked]) : outlines,
    boundingRadius: body.boundingRadius,
    ...primitives,
  };
}

export function instanceMatrixFor(
  placement: InstancerPlacement,
  origin: PlacementOrigin,
  ground: GroundSampler | undefined,
): THREE.Matrix4 {
  const transform = placementWorldTransform(placement, origin);
  const matrix = new THREE.Matrix4();
  matrix.makeRotationY(transform.yawRadians);
  // A small object is dropped bodily onto the surface under its own origin —
  // see `terrain-drape.ts` for why per-vertex draping is wrong for these.
  const y = transform.position.y + (ground ? ground(transform.position.x, transform.position.z) : 0);
  matrix.setPosition(transform.position.x, y, transform.position.z);
  return matrix;
}
